// File: frontend/src/components/Layout/Header.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Box, InputBase, IconButton, Typography, alpha } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import UserMenu from './UserMenu';
import { cyber } from '../../theme';

interface HeaderProps {
    onMenuToggle?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // Focus the search bar with "/" from anywhere, like most security consoles
    useEffect(() => {
        const handleGlobalKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            const isTyping = ['INPUT', 'TEXTAREA'].includes(target.tagName) || target.isContentEditable;
            if (e.key === '/' && !isTyping) {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', handleGlobalKey);
        return () => window.removeEventListener('keydown', handleGlobalKey);
    }, []);

    const handleSearchSubmit = (e: React.FormEvent | React.KeyboardEvent) => {
        e.preventDefault();
        if (searchValue.trim()) {
            navigate(`/?search=${encodeURIComponent(searchValue.trim())}`);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearchSubmit(e);
        }
    };

    return (
        <AppBar position="static" elevation={0}>
            <Toolbar>
                {/* Hamburger menu - only visible on mobile */}
                {onMenuToggle && (
                    <IconButton
                        color="inherit"
                        aria-label="open navigation menu"
                        edge="start"
                        onClick={onMenuToggle}
                        sx={{ mr: 1, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}
                {/* Search Bar */}
                <Box
                    component="form"
                    onSubmit={handleSearchSubmit}
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 2,
                        backgroundColor: alpha(cyber.bgPaper, 0.6),
                        border: `1px solid ${cyber.border}`,
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        '&:focus-within': {
                            borderColor: cyber.primary,
                            boxShadow: `0 0 0 3px ${alpha(cyber.primary, 0.15)}`,
                        },
                        mr: 2,
                        width: { xs: '100%', sm: 'auto' },
                    }}
                >
                    <SearchIcon sx={{ ml: 1.5, color: 'text.secondary', pointerEvents: 'none' }} />
                    <InputBase
                        inputRef={inputRef}
                        placeholder="Search reports by title, source, or keywords..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        sx={{ pl: 1.5, pr: 1, py: 0.75, width: { xs: 'calc(100% - 40px)', sm: '350px' }, fontSize: '0.875rem' }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            display: { xs: 'none', sm: 'block' },
                            mr: 1.5,
                            px: 0.75,
                            py: 0.1,
                            borderRadius: 1,
                            border: `1px solid ${cyber.border}`,
                            color: 'text.secondary',
                            fontFamily: cyber.fontMono,
                            fontSize: '0.7rem',
                        }}
                    >
                        /
                    </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }} />

                <UserMenu />
            </Toolbar>
        </AppBar>
    );
};

export default Header;
